import type { AdminOverviewTenant } from "@/api/tenants";
import type { ColumnDef } from "@/components/SortableTable";
import { SortableTable } from "@/components/SortableTable";
import { Button, Pill } from "@/components/ui";
import { t } from "@/i18n";
import { platformLabel, statusLabel } from "@/lib/labels";
import { fmtDateTime, fmtMoney } from "@/lib/utils";

interface TenantTableProps {
  tenants: AdminOverviewTenant[];
  onSwitch: (tenantId: number) => void;
  switchingId: number | null;
}

/** /admin tenant tablosu — durum, platform, son senkron, hata, 24s is
 * hatasi, AI maliyeti + "Tenant'a gec" (gorev talimati madde 5).
 * 11.09.2026 canli bulgu: "Bu magazaya gec" en sagda + son hata hucresi
 * kirpilmadigi icin (ikas GraphQL hatalari cok uzun) tabloya ulasmak icin
 * cok saga kaydirmak gerekiyordu -> dugme adin hemen yanina alindi, hata
 * hucresi 280px'te kirpilir (tam metin title'da). */
export function TenantTable({ tenants, onSwitch, switchingId }: TenantTableProps) {
  const columns: ColumnDef<AdminOverviewTenant>[] = [
    { key: "name", header: t("admin.tenants.name"), accessor: (r) => r.name, sortValue: (r) => r.name },
    {
      key: "switch",
      header: t("admin.tenants.switch"),
      accessor: (r) => (
        <Button type="button" variant="outline" size="sm" disabled={switchingId === r.id} onClick={() => onSwitch(r.id)}>
          {t("admin.tenants.switch")}
        </Button>
      ),
    },
    { key: "platform", header: t("admin.tenants.platform"), accessor: (r) => platformLabel(r.platform) },
    { key: "status", header: t("admin.tenants.status"), accessor: (r) => statusLabel(r.status) },
    {
      key: "last_success_at",
      header: t("admin.tenants.last_sync"),
      accessor: (r) => fmtDateTime(r.last_success_at),
      sortValue: (r) => r.last_success_at ?? "",
    },
    {
      key: "last_error",
      header: t("admin.tenants.last_error"),
      accessor: (r) =>
        r.last_error ? (
          <Pill tone="bad" title={r.last_error} className="!block max-w-[280px] truncate">
            {r.last_error.startsWith('auth:') ? 'Yeniden bağlantı gerekli' : 'Son veri çekimi başarısız'}
          </Pill>
        ) : (
          "—"
        ),
    },
    {
      key: "job_failed_24h",
      header: t("admin.tenants.job_failures"),
      accessor: (r) => r.job_failed_24h,
      sortValue: (r) => r.job_failed_24h,
    },
    {
      key: "ai_cost_month",
      header: t("admin.tenants.ai_cost"),
      accessor: (r) => fmtMoney(r.ai_cost_month),
      sortValue: (r) => r.ai_cost_month,
    },
  ];

  return <SortableTable columns={columns} rows={tenants} rowKey={(r) => r.id} initialSortKey="name" />;
}
